/** Admin: client-organization group management (Feature 29). */
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../client.js';
import type { AddMembersResult, Group, GroupCollection, GroupMember } from '../types.js';

export function listGroups(organizationId: string): Promise<Group[]> {
  return apiGet<Group[]>(`/api/v1/admin/organizations/${organizationId}/groups`);
}

export function createGroup(
  organizationId: string,
  body: { name: string; description?: string | null },
): Promise<Group> {
  return apiPost<Group>(`/api/v1/admin/organizations/${organizationId}/groups`, body);
}

export function getGroup(organizationId: string, groupId: string): Promise<Group> {
  return apiGet<Group>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}`,
  );
}

export function updateGroup(
  organizationId: string,
  groupId: string,
  body: { name?: string; description?: string | null },
): Promise<Group> {
  return apiPatch<Group>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}`,
    body,
  );
}

export function deleteGroup(
  organizationId: string,
  groupId: string,
): Promise<void> {
  return apiDelete<void>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}`,
  );
}

export function listGroupMembers(
  organizationId: string,
  groupId: string,
): Promise<GroupMember[]> {
  return apiGet<GroupMember[]>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}/members`,
  );
}

export function addGroupMembers(
  organizationId: string,
  groupId: string,
  platformUserIds: string[],
): Promise<AddMembersResult> {
  return apiPost<AddMembersResult>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}/members`,
    { platform_user_ids: platformUserIds },
  );
}

export function removeGroupMember(
  organizationId: string,
  groupId: string,
  platformUserId: string,
): Promise<void> {
  return apiDelete<void>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}/members/${platformUserId}`,
  );
}

/**
 * A user's groups within one organization.
 *
 * These two hit the org-facing routes rather than /api/v1/admin like the
 * rest of this file: one route pair serves both audiences because
 * require_client_admin returns early for platform administrators.
 *
 * `userId` is the platform user id, the same value the admin account
 * detail exposes as `platform_user_id`.
 */
export function listMemberGroups(
  organizationId: string,
  userId: string,
): Promise<Group[]> {
  return apiGet<Group[]>(
    `/api/v1/organizations/${organizationId}/users/${userId}/groups`,
  );
}

export function setMemberGroups(
  organizationId: string,
  userId: string,
  groupIds: string[],
): Promise<Group[]> {
  return apiPut<Group[]>(
    `/api/v1/organizations/${organizationId}/users/${userId}/groups`,
    { group_ids: groupIds },
  );
}

/** Which collections this group grants, and how much of each. */
export function listGroupCollections(
  organizationId: string,
  groupId: string,
): Promise<GroupCollection[]> {
  return apiGet<GroupCollection[]>(
    `/api/v1/admin/organizations/${organizationId}/groups/${groupId}/collections`,
  );
}
